import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import App from "./App";
import { ApiError } from "./api/client";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "./tokens.css";
import "./app.css";

// Built before the client because its callback refers to it. The reference is
// only read when a query fails, long after both exist.
const queryCache = new QueryCache({
  onError: (error) => {
    // A 401 on any query means the session ended mid-visit — expired, or revoked
    // server-side. Without this the cached `me` keeps RequireAuth rendering the
    // app while each page blames the data for what is actually an auth problem.
    if (error instanceof ApiError && error.status === 401) {
      queryClient.invalidateQueries({ queryKey: ["session"] });
    }
  },
});

const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      // A 401 means the session ended; retrying it just delays the login page.
      // A 404 is never transient either — on the trader page it is a designed
      // state with its own copy, and retrying it just delays that message
      // behind two rounds of backoff while a skeleton sits where the "no
      // trader by that name" hint belongs.
      retry: (failureCount, error) =>
        !(error instanceof ApiError &&
          (error.status === 401 || error.status === 403 || error.status === 404)) &&
        failureCount < 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
